import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { SimulationEngine } from '../simulation/SimulationEngine';
import { DensityType } from '../simulation/types';
import { TerrainRenderer } from './TerrainRenderer';
import { RoadRenderer } from './RoadRenderer';
import { BuildingRenderer } from './BuildingRenderer';
import { LifeRenderer } from './LifeRenderer';
import { DayNightShader } from './DayNightShader';

export class ThreeRenderer {
  private container: HTMLElement;
  private simEngine: SimulationEngine;

  public scene!: THREE.Scene;
  public camera!: THREE.OrthographicCamera;
  public renderer!: THREE.WebGLRenderer;
  public controls!: OrbitControls;

  // Lights
  private ambientLight!: THREE.AmbientLight;
  private sunLight!: THREE.DirectionalLight;

  // Sub-renderers
  private terrainRenderer!: TerrainRenderer;
  private roadRenderer!: RoadRenderer;
  private buildingRenderer!: BuildingRenderer;
  private lifeRenderer!: LifeRenderer;
  private dayNightShader!: DayNightShader;

  // Interaction & Raycasting
  public activeTool = 'select';
  public activeOverlay = 'none';
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private hoverMarker!: THREE.Mesh;

  // Drag-painting state
  private isPointerDown = false;
  private lastPaintGrid = { x: -1, y: -1 };
  private activePointerButton = -1;

  // Animation frame ID
  private animationFrameId: number | null = null;
  private clock = new THREE.Clock();

  // Target zoom scale for Orthographic Camera
  private targetZoom = 1.0;
  private onCellSelect?: (x: number, y: number) => void;

  constructor(containerId: string, simEngine: SimulationEngine, onCellSelect?: (x: number, y: number) => void) {
    const parent = document.getElementById(containerId);
    if (!parent) {
      throw new Error(`[ThreeRenderer] Container element with ID "${containerId}" not found.`);
    }
    this.container = parent;
    this.simEngine = simEngine;
    this.onCellSelect = onCellSelect;

    this.initThree();
    this.initLights();
    this.initHoverMarker();
    this.initSubRenderers();
    this.setupInputListeners();
    this.startRenderingLoop();

    console.log("[DEBUG] [ThreeRenderer] Initialization complete.");
  }

  private initThree() {
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;

    // 1. Create Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x263238); // Sleek slate background

    // 2. Create Orthographic Camera (tabletop isometric view: 45° azimuth, 35° elevation)
    const aspect = width / height;
    const frustumSize = 12.5;
    this.camera = new THREE.OrthographicCamera(
      -frustumSize * aspect,
      frustumSize * aspect,
      frustumSize,
      -frustumSize,
      0.1,
      1000
    );
    this.camera.zoom = 1.0;
    
    // Position camera dynamically based on grid size
    const state = this.simEngine.getState();
    const halfWidth = state.width / 2;
    const halfHeight = state.height / 2;
    
    // Angled look down from South-East (X and Z positive, Y high)
    this.camera.position.set(halfWidth + 20, 20, halfHeight + 20);

    // 3. Create WebGL Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Beautiful soft shadows
    
    // Clear any existing children in container
    this.container.innerHTML = '';
    this.container.appendChild(this.renderer.domElement);

    // 4. Create OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0); // Center of diorama
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.05; // Don't orbit below ground
    this.controls.minPolarAngle = 0.1; // Don't look straight down from top
    this.controls.minDistance = 2;
    this.controls.maxDistance = 100;
    
    // Resize listener
    window.addEventListener('resize', this.handleResize);
  }

  private initLights() {
    // Warm gentle ambient light
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.65);
    this.scene.add(this.ambientLight);

    // Beautiful sun light for casting cinematic shadows
    this.sunLight = new THREE.DirectionalLight(0xfff9c4, 1.2); // Warm sunlight
    this.sunLight.position.set(15, 25, 15);
    this.sunLight.castShadow = true;

    // Configure shadows
    this.sunLight.shadow.mapSize.width = 2048;
    this.sunLight.shadow.mapSize.height = 2048;
    this.sunLight.shadow.camera.near = 0.5;
    this.sunLight.shadow.camera.far = 60;
    
    const d = 25; // Shadow ortho box size
    this.sunLight.shadow.camera.left = -d;
    this.sunLight.shadow.camera.right = d;
    this.sunLight.shadow.camera.top = d;
    this.sunLight.shadow.camera.bottom = -d;
    this.sunLight.shadow.bias = -0.0004;

    this.scene.add(this.sunLight);
  }

  private initHoverMarker() {
    // 3D wireframe bounding box that sits flat on grid
    const geo = new THREE.BoxGeometry(0.96, 0.05, 0.96);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x00e676,
      transparent: true,
      opacity: 0.35,
      wireframe: false
    });

    this.hoverMarker = new THREE.Mesh(geo, mat);
    this.hoverMarker.position.set(0, 0.025, 0);
    this.hoverMarker.visible = false;
    this.scene.add(this.hoverMarker);
  }

  private initSubRenderers() {
    const state = this.simEngine.getState();

    // Setup sub-renderers
    this.terrainRenderer = new TerrainRenderer(this.scene);
    this.roadRenderer = new RoadRenderer(this.scene);
    this.buildingRenderer = new BuildingRenderer(this.scene);
    this.lifeRenderer = new LifeRenderer(this.scene);
    this.dayNightShader = new DayNightShader(this.ambientLight, this.sunLight);

    // Initial render
    this.terrainRenderer.render(state);
    this.roadRenderer.render(state);
    this.buildingRenderer.render(state);

    // Wire up Sim Engine state updates
    this.simEngine.on('state-changed', (updatedState) => {
      this.terrainRenderer.render(updatedState);
      this.roadRenderer.render(updatedState);
      this.buildingRenderer.render(updatedState);
    });

    this.simEngine.on('tick', (updatedState) => {
      this.roadRenderer.render(updatedState);
      this.buildingRenderer.render(updatedState);
    });
  }

  private handleResize = () => {
    if (!this.container) return;
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    const aspect = width / height;

    const frustumSize = 12.5;
    this.camera.left = -frustumSize * aspect;
    this.camera.right = frustumSize * aspect;
    this.camera.top = frustumSize;
    this.camera.bottom = -frustumSize;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
  };

  private setupInputListeners() {
    this.container.addEventListener('pointerdown', this.onPointerDownEvent);
    this.container.addEventListener('pointermove', this.onPointerMoveEvent);
    this.container.addEventListener('pointerup', this.onPointerUpEvent);
    this.container.addEventListener('contextmenu', this.onContextMenu);
  }

  private onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };

  private onPointerDownEvent = (e: PointerEvent) => {
    // If active tool is 'select' and button is left-click, select the cell under pointer
    if (this.activeTool === 'select' && e.button === 0) {
      this.updateMouseCoords(e);
      const grid = this.getGridIntersection();
      if (grid && this.onCellSelect) {
        this.onCellSelect(grid.x, grid.y);
      } else if (this.onCellSelect) {
        this.onCellSelect(-1, -1); // Deselect
      }
      this.activePointerButton = e.button;
      return;
    }

    // We only drag-paint if active tool is not 'select' and button is left-click (0)
    if (this.activeTool === 'select' || e.button !== 0) {
      this.activePointerButton = e.button;
      return;
    }

    this.isPointerDown = true;
    this.activePointerButton = e.button;
    this.lastPaintGrid = { x: -1, y: -1 };

    this.paintAtPointer(e);
  };

  private onPointerMoveEvent = (e: PointerEvent) => {
    this.updateMouseCoords(e);
    this.updateHoverHighlight();

    if (this.isPointerDown && this.activePointerButton === 0) {
      this.paintAtPointer(e);
    }
  };

  private onPointerUpEvent = (e: PointerEvent) => {
    if (e.button === 0) {
      this.isPointerDown = false;
      this.lastPaintGrid = { x: -1, y: -1 };
    }
    this.activePointerButton = -1;
  };

  private updateMouseCoords(e: PointerEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private getGridIntersection(): { x: number; y: number } | null {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersectionPoint = new THREE.Vector3();
    
    // Intersect our virtual flat infinite ground plane sitting at Y = 0
    if (this.raycaster.ray.intersectPlane(this.groundPlane, intersectionPoint)) {
      const state = this.simEngine.getState();
      const halfWidth = state.width / 2;
      const halfHeight = state.height / 2;

      // Translate 3D space back into grid space
      // worldX = x - width/2 + 0.5 => x = worldX + width/2 - 0.5
      const gridX = Math.floor(intersectionPoint.x + halfWidth);
      const gridZ = Math.floor(intersectionPoint.z + halfHeight);

      if (gridX >= 0 && gridX < state.width && gridZ >= 0 && gridZ < state.height) {
        return { x: gridX, y: gridZ };
      }
    }
    return null;
  }

  private isPlacementValid(x: number, y: number, tool: string): boolean {
    const state = this.simEngine.getState();
    const key = `${x},${y}`;

    // Out of bounds check
    if (x < 0 || x >= state.width || y < 0 || y >= state.height) return false;

    // Check terrain (0 = water, 1 = sand, 2 = grass, 3 = rock)
    const terrainType = state.terrain[y]?.[x];

    if (tool === 'demolish') {
      // Demolish is valid if there is a road, building, or parcel
      return !!state.roads[key] || !!state.buildings[key] || !!state.parcels[key];
    }

    // Painting / construction requires empty grass/sand, no existing roads, and no existing buildings
    if (terrainType !== 1 && terrainType !== 2) return false;
    if (state.roads[key] || state.buildings[key]) return false;

    if (tool.startsWith('zone_')) {
      // Zoning requires no existing parcel
      return !state.parcels[key];
    }

    return true;
  }

  public rotateCamera(direction: 'left' | 'right') {
    const angle = direction === 'left' ? -Math.PI / 2 : Math.PI / 2;
    
    const pos = this.camera.position.clone().sub(this.controls.target);
    
    // Rotate coordinates around target center (Y-axis horizontal rotation)
    const rotatedX = pos.x * Math.cos(angle) - pos.z * Math.sin(angle);
    const rotatedZ = pos.x * Math.sin(angle) + pos.z * Math.cos(angle);
    
    this.camera.position.set(
      rotatedX + this.controls.target.x,
      this.camera.position.y,
      rotatedZ + this.controls.target.z
    );
    this.controls.update();
    console.log(`[ThreeRenderer] Rotated camera 90° ${direction.toUpperCase()}`);
  }

  private updateHoverHighlight() {
    if (this.activeTool === 'select') {
      this.hoverMarker.visible = false;
      this.container.style.cursor = 'default';
      return;
    }

    this.container.style.cursor = 'grab';
    const grid = this.getGridIntersection();

    if (grid) {
      const state = this.simEngine.getState();
      const halfWidth = state.width / 2;
      const halfHeight = state.height / 2;

      // Map back to 3D world space coordinate
      const worldX = grid.x - halfWidth + 0.5;
      const worldZ = grid.y - halfHeight + 0.5;

      this.hoverMarker.position.set(worldX, 0.025, worldZ);
      this.hoverMarker.visible = true;

      // High-Fidelity validity indicator: Green for Valid, Red for Invalid placement!
      const isValid = this.isPlacementValid(grid.x, grid.y, this.activeTool);
      let color = isValid ? 0x00e676 : 0xff1744; // pocket city style neon green vs warning red

      (this.hoverMarker.material as THREE.MeshBasicMaterial).color.setHex(color);
    } else {
      this.hoverMarker.visible = false;
    }
  }

  private paintAtPointer(_e: PointerEvent) {
    const grid = this.getGridIntersection();
    if (!grid) return;

    // Prevent double painting the exact same cell in one click/drag sweep
    if (grid.x === this.lastPaintGrid.x && grid.y === this.lastPaintGrid.y) return;

    this.lastPaintGrid = { x: grid.x, y: grid.y };
    this.executeToolAction(grid.x, grid.y);
  }

  private executeToolAction(x: number, y: number) {
    if (!this.isPlacementValid(x, y, this.activeTool)) return; // Don't build if placement is invalid!

    const civicTools = [
      'park', 'police', 'fire', 'hospital', 'school',
      'power_solar', 'power_coal', 'water_tower', 'water_pump',
      'port', 'airport', 'central_station', 'panoramic_tower', 'stadium'
    ];

    if (this.activeTool === 'road') {
      this.simEngine.buildRoad(x, y, 'local');
    } else if (this.activeTool === 'demolish') {
      this.simEngine.demolish(x, y);
      this.terrainRenderer.setPropVisibility(x, y, true); // restore tree checks
    } else if (civicTools.includes(this.activeTool)) {
      this.simEngine.buildCivic(x, y, this.activeTool);
      this.terrainRenderer.setPropVisibility(x, y, false); // hide tree props if building
    } else if (this.activeTool === 'civic') {
      this.simEngine.buildCivic(x, y, 'civic');
      this.terrainRenderer.setPropVisibility(x, y, false);
    } else if (this.activeTool.startsWith('zone_residential_')) {
      const density = this.activeTool.replace('zone_residential_', '') as DensityType;
      this.simEngine.paintZone(x, y, 'residential', density);
    } else if (this.activeTool.startsWith('zone_commercial_')) {
      const density = this.activeTool.replace('zone_commercial_', '') as DensityType;
      this.simEngine.paintZone(x, y, 'commercial', density);
    } else if (this.activeTool.startsWith('zone_industrial_')) {
      const density = this.activeTool.replace('zone_industrial_', '') as DensityType;
      this.simEngine.paintZone(x, y, 'industrial', density);
    }
  }

  private startRenderingLoop() {
    const animate = () => {
      this.animationFrameId = requestAnimationFrame(animate);

      const delta = this.clock.getDelta();
      const time = this.clock.getElapsedTime();
      const state = this.simEngine.getState();

      // 1. Update controls damping
      this.controls.update();

      // 2. Camera LERP Zoom (Orthographic)
      if (Math.abs(this.camera.zoom - this.targetZoom) > 0.01) {
        this.camera.zoom = THREE.MathUtils.lerp(this.camera.zoom, this.targetZoom, 0.08);
        this.camera.updateProjectionMatrix();
      }

      // 3. Update Day/Night Shader & Lights
      this.dayNightShader.update(time, delta, state.simSpeed);
      (window as any).swisslandFormattedTime = this.dayNightShader.getFormattedTime();

      // 4. Update dynamic cars/citizens
      this.lifeRenderer.update(state, this.simEngine.getRoadEngine(), delta);

      // 4.5. Update building animations (spawning, pulsing, shrinking)
      this.buildingRenderer.update(delta);

      // 5. Update terrain zoning transparent overlays and active heatmaps
      this.terrainRenderer.updateZones(state);
      this.terrainRenderer.updateHeatmaps(state, this.activeOverlay);

      // 6. Draw WebGL
      this.renderer.render(this.scene, this.camera);
    };

    animate();
  }

  public triggerZoom(level: 'macro' | 'meso' | 'micro') {
    // Adjust targetZoom so camera zooms in/out smoothly on next render ticks
    if (level === 'macro') {
      this.targetZoom = 0.55;
    } else if (level === 'meso') {
      this.targetZoom = 1.0;
    } else if (level === 'micro') {
      this.targetZoom = 2.2;
    }
  }

  public destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    window.removeEventListener('resize', this.handleResize);
    this.container.removeEventListener('pointerdown', this.onPointerDownEvent);
    this.container.removeEventListener('pointermove', this.onPointerMoveEvent);
    this.container.removeEventListener('pointerup', this.onPointerUpEvent);
    this.container.removeEventListener('contextmenu', this.onContextMenu);
    
    this.renderer.dispose();
    this.terrainRenderer.clear();
    this.roadRenderer.clear();
    this.buildingRenderer.clear();
    this.lifeRenderer.clear();
  }
}
